import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import AdminService from '../../services/adminService';
import { LoadingSpinner, ActionButton } from '../ui';

const emptyForm = {
  title: '',
  summary: '',
  body: '',
  changeNotes: ''
};

const defaultCostModel = {
  currency: 'LKR',
  components: []
};

const PathwayVersionsModal = ({ pathway, onClose, onRefresh }) => {
  const [versions, setVersions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [sections, setSections] = useState([]);
  const [eligibilityRules, setEligibilityRules] = useState([]);
  const [costModel, setCostModel] = useState(defaultCostModel);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  // Markdown preview modes: 'edit' | 'preview' | 'split'
  const [bodyView, setBodyView] = useState(() => {
    try {
      return localStorage.getItem('pathway_bodyView') || 'split';
    } catch (e) {
      return 'split';
    }
  });

  // Lazy-load languages for syntax highlighting only when needed
  const CodeBlock = ({ className, children, ...props }) => {
    const langMatch = /language-(\w+)/.exec(className || '');
    const rawLang = (langMatch && langMatch[1]) || 'text';
    const langMap = {
      js: 'javascript', javascript: 'javascript',
      ts: 'typescript', typescript: 'typescript',
      sh: 'bash', bash: 'bash',
      md: 'markdown', markdown: 'markdown',
      yml: 'yaml', yaml: 'yaml',
      json: 'json'
    };
    const langKey = langMap[rawLang] || rawLang;

    const [ready, setReady] = useState(false);

    useEffect(() => {
      let cancelled = false;
      const loaders = {
        javascript: () => import('react-syntax-highlighter/dist/esm/languages/prism/javascript'),
        typescript: () => import('react-syntax-highlighter/dist/esm/languages/prism/typescript'),
        json: () => import('react-syntax-highlighter/dist/esm/languages/prism/json'),
        bash: () => import('react-syntax-highlighter/dist/esm/languages/prism/bash'),
        markdown: () => import('react-syntax-highlighter/dist/esm/languages/prism/markdown'),
        yaml: () => import('react-syntax-highlighter/dist/esm/languages/prism/yaml'),
      };
      const loader = loaders[langKey];
      if (!loader) {
        setReady(true);
        return;
      }
      loader()
        .then((mod) => {
          if (mod && mod.default) {
            SyntaxHighlighter.registerLanguage(langKey, mod.default);
          }
          if (!cancelled) setReady(true);
        })
        .catch(() => {
          if (!cancelled) setReady(true);
        });
      return () => {
        cancelled = true;
      };
    }, [langKey]);

    if (!ready) {
      return (
        <pre className={className} {...props}>
          <code>{String(children)}</code>
        </pre>
      );
    }

    return (
      <SyntaxHighlighter
        style={vscDarkPlus}
        language={langKey}
        PreTag="div"
        customStyle={{ margin: 0, background: 'transparent' }}
        {...props}
      >
        {String(children).replace(/\n$/, '')}
      </SyntaxHighlighter>
    );
  };

  // Shared markdown components with syntax highlighting
  const markdownComponents = useMemo(() => ({
    code({ inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      if (!inline && match) {
        return <CodeBlock className={className} {...props}>{children}</CodeBlock>;
      }
      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    },
  }), []);

  useEffect(() => {
    try {
      localStorage.setItem('pathway_bodyView', bodyView);
    } catch (e) {
      // ignore persistence issues
    }
  }, [bodyView]);

  const selectedVersion = useMemo(
    () => versions.find((v) => v.id === selectedId) || null,
    [selectedId, versions]
  );

  // Diff state
  const [compareA, setCompareA] = useState(null);
  const [compareB, setCompareB] = useState(null);
  const [showDiff, setShowDiff] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);

  useEffect(() => {
    const loadVersions = async () => {
      try {
        setLoading(true);
        const data = await AdminService.getPathwayVersions(pathway.id);
        setVersions(data);
        if (data.length) {
          setSelectedId(data[0].id);
        }
        setError('');
      } catch (err) {
        console.error('Error loading pathway versions:', err);
        setError('Failed to load pathway versions.');
      } finally {
        setLoading(false);
      }
    };

    loadVersions();
  }, [pathway.id]);

  useEffect(() => {
    if (!selectedVersion) {
      setForm(emptyForm);
      setSections([]);
      setEligibilityRules([]);
      setCostModel(defaultCostModel);
      return;
    }
    const body = selectedVersion.content?.body || '';
    const versionSections = Array.isArray(selectedVersion.content?.sections)
      ? selectedVersion.content.sections
      : [];
    const rules = Array.isArray(selectedVersion.eligibility_rules)
      ? selectedVersion.eligibility_rules
      : [];
    const cm = selectedVersion.cost_model && typeof selectedVersion.cost_model === 'object'
      ? {
        currency: selectedVersion.cost_model.currency || 'LKR',
        components: Array.isArray(selectedVersion.cost_model.components)
          ? selectedVersion.cost_model.components
          : []
      }
      : defaultCostModel;
    setForm({
      title: selectedVersion.title || '',
      summary: selectedVersion.summary || '',
      body,
      changeNotes: selectedVersion.change_notes || ''
    });
    setSections(versionSections);
    setEligibilityRules(rules);
    setCostModel(cm);

    // default compare targets: latest (A) vs last published (B)
    if (versions.length > 0) {
      const latest = versions[0]?.id || null;
      const published = versions.find((v) => v.status === 'published')?.id || null;
      setCompareA(latest);
      setCompareB(published && published !== latest ? published : (versions[1]?.id || null));
    }
  }, [selectedVersion]);

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const addSection = () => {
    setSections((prev) => ([
      ...prev,
      {
        key: `section_${Date.now()}`,
        title: '',
        body: ''
      }
    ]));
  };

  const updateSection = (index, field, value) => {
    setSections((prev) => prev.map((section, idx) => (
      idx === index ? { ...section, [field]: value } : section
    )));
  };

  const removeSection = (index) => {
    setSections((prev) => prev.filter((_, idx) => idx !== index));
  };

  const operatorOptions = ['>=', '<=', '==', '!=', '>', '<'];
  const severityOptions = ['info', 'warning', 'critical'];

  const addEligibilityRule = () => {
    setEligibilityRules((prev) => ([
      ...prev,
      {
        id: `rule_${Date.now()}`,
        attribute: '',
        operator: '>=',
        value: '',
        severity: 'warning',
        note: ''
      }
    ]));
  };

  const updateEligibilityRule = (index, field, value) => {
    setEligibilityRules((prev) => prev.map((rule, idx) => (
      idx === index ? { ...rule, [field]: value } : rule
    )));
  };

  const removeEligibilityRule = (index) => {
    setEligibilityRules((prev) => prev.filter((_, idx) => idx !== index));
  };

  const addCostComponent = () => {
    setCostModel((prev) => ({
      ...prev,
      components: [
        ...(prev.components || []),
        {
          id: `cost_${Date.now()}`,
          label: '',
          min: '',
          max: '',
          currency: prev.currency || 'LKR',
          recurring: false,
          payable_to: '',
          notes: ''
        }
      ]
    }));
  };

  const updateCostComponent = (index, field, value) => {
    setCostModel((prev) => ({
      ...prev,
      components: prev.components.map((component, idx) => (
        idx === index
          ? {
            ...component,
            [field]: field === 'recurring'
              ? value
              : field === 'min' || field === 'max'
                ? (value === '' ? '' : Number(value))
                : value
          }
          : component
      ))
    }));
  };

  const removeCostComponent = (index) => {
    setCostModel((prev) => ({
      ...prev,
      components: prev.components.filter((_, idx) => idx !== index)
    }));
  };

  const handleCostCurrencyChange = (currency) => {
    setCostModel((prev) => ({
      ...prev,
      currency,
      components: prev.components.map((component) => ({ ...component, currency }))
    }));
  };

  const handleSave = async () => {
    if (!selectedVersion) return;
    try {
      setSaving(true);
      setError('');
      setInfo('');
      const content = {
        ...(selectedVersion.content || {}),
        body: form.body,
        sections
      };

      await AdminService.updatePathwayVersion(selectedVersion.id, {
        title: form.title,
        summary: form.summary,
        change_notes: form.changeNotes || null,
        content,
        eligibility_rules: eligibilityRules,
        cost_model: {
          ...(costModel || {}),
          currency: costModel.currency || 'LKR',
          components: (costModel.components || []).map((component) => ({
            ...component,
            min: component.min === '' || component.min === null ? null : Number(component.min),
            max: component.max === '' || component.max === null ? null : Number(component.max)
          }))
        }
      });

      setInfo('Draft saved.');
      const data = await AdminService.getPathwayVersions(pathway.id);
      setVersions(data);
      if (data.length) {
        setSelectedId(data.find((v) => v.id === selectedVersion.id)?.id || data[0].id);
      }
    } catch (err) {
      console.error('Error saving pathway version:', err);
      setError('Failed to save version.');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = () => {
    if (!selectedVersion || selectedVersion.status === 'published') return;
    setShowDiff(true);
    setCompareA(selectedId);
    const idx = versions.findIndex((v) => v.id === selectedId);
    setCompareB(versions.find((v) => v.status === 'published')?.id || versions[idx + 1]?.id || null);
    setShowPublishConfirm(true);
  };

  const confirmPublish = async () => {
    if (!selectedVersion) return;
    try {
      setPublishing(true);
      setError('');
      setInfo('');
      await AdminService.publishPathwayVersion(selectedVersion.id, pathway.id, form.changeNotes || null);
      setInfo('Version published.');
      setShowPublishConfirm(false);
      const data = await AdminService.getPathwayVersions(pathway.id);
      setVersions(data);
      if (data.length) {
        setSelectedId(data[0].id);
      }
      if (typeof onRefresh === 'function') {
        onRefresh();
      }
    } catch (err) {
      console.error('Error publishing pathway version:', err);
      setError('Failed to publish version.');
    } finally {
      setPublishing(false);
    }
  };

  const handleClone = async () => {
    if (!selectedVersion) return;
    try {
      setCloning(true);
      setError('');
      setInfo('');
      const cloned = await AdminService.clonePathwayVersion(selectedVersion.id);
      setInfo(`Cloned v${selectedVersion.version_number} to v${cloned.version_number}.`);
      const data = await AdminService.getPathwayVersions(pathway.id);
      setVersions(data);
      if (data.length) setSelectedId(data[0].id);
    } catch (err) {
      console.error('Error cloning version:', err);
      setError('Failed to clone version.');
    } finally {
      setCloning(false);
    }
  };

  const simpleDiff = (a, b) => {
    if (a === b) return null;
    if (typeof a === 'string' && typeof b === 'string') {
      return { a, b };
    }
    try {
      const aa = JSON.stringify(a ?? null, null, 2);
      const bb = JSON.stringify(b ?? null, null, 2);
      if (aa === bb) return null;
      return { a: aa, b: bb };
    } catch {
      return { a: String(a), b: String(b) };
    }
  };

  const computeDiffFlags = (A, B) => {
    if (!A || !B) return null;
    const bodyA = A.content?.body || '';
    const bodyB = B.content?.body || '';
    const sectionsA = Array.isArray(A.content?.sections) ? A.content.sections : [];
    const sectionsB = Array.isArray(B.content?.sections) ? B.content.sections : [];
    const sectionsEqual = (() => {
      try {
        return JSON.stringify(sectionsA) === JSON.stringify(sectionsB);
      } catch {
        return false;
      }
    })();
    const rulesEqual = (() => {
      try {
        return JSON.stringify(A.eligibility_rules || []) === JSON.stringify(B.eligibility_rules || []);
      } catch {
        return false;
      }
    })();
    const costEqual = (() => {
      try {
        return JSON.stringify(A.cost_model || null) === JSON.stringify(B.cost_model || null);
      } catch {
        return false;
      }
    })();

    return {
      title: A.title !== B.title,
      summary: A.summary !== B.summary,
      body: bodyA !== bodyB,
      sections: !sectionsEqual,
      rules: !rulesEqual,
      cost: !costEqual,
    };
  };

  const getVersionById = (id) => versions.find((v) => v.id === id) || null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto border border-gray-700">
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white">Pathway Versions — {pathway.pathway_key}</h3>
            <p className="text-sm text-gray-400">Manage draft and published versions for this pathway.</p>
          </div>
          <button onClick={onClose} className="text-sm text-gray-400 hover:text-white">✕ Close</button>
        </div>

        <div className="px-6 py-4">
          {showPublishConfirm && selectedVersion && (
            <div className="mb-4 border rounded-lg p-3 bg-yellow-900/20 border-yellow-800">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-yellow-400">Publish confirmation</div>
                <div className="flex items-center gap-2">
                  <button
                    className="text-xs px-2 py-1 rounded border border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-600"
                    onClick={() => setShowPublishConfirm(false)}
                  >Cancel</button>
                  <button
                    className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                    onClick={confirmPublish}
                    disabled={publishing}
                  >{publishing ? 'Publishing…' : 'Confirm Publish'}</button>
                </div>
              </div>
              <div className="text-xs text-yellow-500 mt-2">Review changes below before confirming publish.</div>
            </div>
          )}
          {loading ? (
            <div className="py-16">
              <LoadingSpinner message="Loading versions..." />
            </div>
          ) : versions.length === 0 ? (
            <div className="text-sm text-gray-400">No versions yet. Create a draft to begin editing.</div>
          ) : (
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12 md:col-span-4">
                <div className="bg-gray-900/30 border border-gray-700 rounded-lg divide-y divide-gray-700">
                  {versions.map((v) => {
                    const pub = versions.find((x) => x.status === 'published') || null;
                    const flags = pub ? computeDiffFlags(v, pub) : null;
                    const changedCount = flags ? Object.values(flags).filter(Boolean).length : 0;
                    return (
                      <button
                        key={v.id}
                        onClick={() => setSelectedId(v.id)}
                        className={`w-full text-left px-4 py-3 text-sm transition-colors ${v.id === selectedId ? 'bg-gray-700 border-l-4 border-blue-500' : 'hover:bg-gray-700/50 text-gray-300'
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className={`font-medium ${v.id === selectedId ? 'text-white' : 'text-gray-300'}`}>v{v.version_number}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${v.status === 'published'
                            ? 'bg-green-900/30 text-green-400 border border-green-800'
                            : 'bg-yellow-900/30 text-yellow-400 border border-yellow-800'
                            }`}>
                            {v.status}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {v.published_at ? `Published ${new Date(v.published_at).toLocaleString()}` : 'Draft not published'}
                        </div>
                        {pub && v.id !== pub.id && changedCount > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {flags.title && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-900/30 text-blue-400 border border-blue-800">title</span>}
                            {flags.summary && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-900/30 text-blue-400 border border-blue-800">summary</span>}
                            {flags.body && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-900/30 text-purple-400 border border-purple-800">body</span>}
                            {flags.sections && <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-900/30 text-indigo-400 border border-indigo-800">sections</span>}
                            {flags.rules && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-400 border border-amber-800">rules</span>}
                            {flags.cost && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-900/30 text-emerald-400 border border-emerald-800">cost</span>}
                          </div>
                        )}
                        {v.change_notes && (
                          <div className="text-xs text-gray-500 mt-1 line-clamp-2">{v.change_notes}</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="col-span-12 md:col-span-8">
                {!selectedVersion ? (
                  <div className="text-sm text-gray-400">Select a version to edit.</div>
                ) : (
                  <div className="space-y-4">
                    {(error || info) && (
                      <div className={`p-3 rounded border ${error ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-green-900/20 border-green-800 text-green-400'}`}>
                        {error || info}
                      </div>
                    )}

                    <div className="grid grid-cols-1 gap-3">
                      <label className="text-xs font-semibold text-gray-400 uppercase">Title</label>
                      <input
                        className="border rounded px-3 py-2 text-sm bg-gray-700 border-gray-600 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        value={form.title}
                        onChange={(e) => handleFieldChange('title', e.target.value)}
                        disabled={selectedVersion.status === 'published'}
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      <label className="text-xs font-semibold text-gray-400 uppercase">Summary</label>
                      <textarea
                        className="border rounded px-3 py-2 text-sm h-20 bg-gray-700 border-gray-600 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        value={form.summary}
                        onChange={(e) => handleFieldChange('summary', e.target.value)}
                        disabled={selectedVersion.status === 'published'}
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-semibold text-gray-400 uppercase">Body (Markdown)</label>
                        <div className="flex items-center gap-1 text-xs">
                          <button
                            type="button"
                            onClick={() => setBodyView('edit')}
                            className={`px-2 py-1 rounded ${bodyView === 'edit' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => setBodyView('preview')}
                            className={`px-2 py-1 rounded ${bodyView === 'preview' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                          >
                            Preview
                          </button>
                          <button
                            type="button"
                            onClick={() => setBodyView('split')}
                            className={`px-2 py-1 rounded ${bodyView === 'split' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                          >
                            Split
                          </button>
                        </div>
                      </div>

                      {bodyView === 'edit' && (
                        <textarea
                          className="border rounded px-3 py-2 text-sm h-48 font-mono bg-gray-700 border-gray-600 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          value={form.body}
                          onChange={(e) => handleFieldChange('body', e.target.value)}
                          disabled={selectedVersion.status === 'published'}
                        />
                      )}

                      {bodyView === 'preview' && (
                        <div className="border rounded p-3 bg-gray-900/50 border-gray-700 prose prose-invert prose-sm max-w-none">
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                            {form.body || ''}
                          </ReactMarkdown>
                        </div>
                      )}

                      {bodyView === 'split' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <textarea
                            className="border rounded px-3 py-2 text-sm h-48 font-mono bg-gray-700 border-gray-600 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            value={form.body}
                            onChange={(e) => handleFieldChange('body', e.target.value)}
                            disabled={selectedVersion.status === 'published'}
                          />
                          <div className="border rounded p-3 bg-gray-900/50 border-gray-700 prose prose-invert prose-sm max-w-none overflow-y-auto h-48">
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                              {form.body || ''}
                            </ReactMarkdown>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Diff Panel */}
                    <div className="border rounded-lg p-4 space-y-3 bg-gray-900/30 border-gray-700">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-gray-300">Version Diff</h4>
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-400">Show</label>
                          <input type="checkbox" checked={showDiff} onChange={(e) => setShowDiff(e.target.checked)} className="bg-gray-700 border-gray-600 rounded" />
                          {showDiff && (
                            <>
                              <button
                                type="button"
                                className="text-xs px-2 py-1 rounded border border-gray-600 text-gray-300 hover:bg-gray-700"
                                onClick={() => {
                                  const latest = versions[0]?.id || null;
                                  const published = versions.find((v) => v.status === 'published')?.id || null;
                                  setCompareA(latest);
                                  setCompareB(published && published !== latest ? published : (versions[1]?.id || null));
                                }}
                              >Latest vs Published</button>
                              <button
                                type="button"
                                className="text-xs px-2 py-1 rounded border border-gray-600 text-gray-300 hover:bg-gray-700"
                                onClick={() => {
                                  const currentIdx = versions.findIndex((v) => v.id === selectedId);
                                  const prev = currentIdx >= 0 ? versions[currentIdx + 1]?.id : null;
                                  setCompareA(selectedId);
                                  setCompareB(prev || null);
                                }}
                              >Current vs Previous</button>
                              <button
                                type="button"
                                className="text-xs px-2 py-1 rounded border border-gray-600 text-gray-300 hover:bg-gray-700"
                                onClick={() => {
                                  setCompareA(compareB);
                                  setCompareB(compareA);
                                }}
                              >Swap</button>
                            </>
                          )}
                        </div>
                      </div>
                      {showDiff && (
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <select className="border rounded px-2 py-1 text-xs bg-gray-700 border-gray-600 text-white" value={compareA || ''} onChange={(e) => setCompareA(e.target.value || null)}>
                              <option value="">Select A (left)</option>
                              {versions.map((v) => (
                                <option key={v.id} value={v.id}>v{v.version_number} • {v.status}</option>
                              ))}
                            </select>
                            <select className="border rounded px-2 py-1 text-xs bg-gray-700 border-gray-600 text-white" value={compareB || ''} onChange={(e) => setCompareB(e.target.value || null)}>
                              <option value="">Select B (right)</option>
                              {versions.map((v) => (
                                <option key={v.id} value={v.id}>v{v.version_number} • {v.status}</option>
                              ))}
                            </select>
                          </div>
                          {(() => {
                            const A = getVersionById(compareA);
                            const B = getVersionById(compareB);
                            if (!A || !B) return <div className="text-xs text-gray-400">Pick two versions to compare.</div>;

                            const titleChanged = !!simpleDiff(A.title, B.title);
                            const summaryChanged = !!simpleDiff(A.summary, B.summary);
                            const bodyChanged = !!simpleDiff(A.content?.body || '', B.content?.body || '');

                            const sectionsA = Array.isArray(A.content?.sections) ? A.content.sections : [];
                            const sectionsB = Array.isArray(B.content?.sections) ? B.content.sections : [];
                            const byKey = (arr) => {
                              const out = {};
                              arr.forEach((s, idx) => {
                                const key = s.key || `index_${idx}`;
                                out[key] = s;
                              });
                              return out;
                            };
                            const mapA = byKey(sectionsA);
                            const mapB = byKey(sectionsB);
                            const allKeys = Array.from(new Set([...Object.keys(mapA), ...Object.keys(mapB)]));
                            const sectionDiffItems = allKeys.map((k) => ({ key: k, a: mapA[k] || null, b: mapB[k] || null }));
                            const sectionsChanged = sectionDiffItems.some(({ a, b }) => JSON.stringify(a || null) !== JSON.stringify(b || null));

                            const rulesChanged = !!simpleDiff(A.eligibility_rules || [], B.eligibility_rules || []);
                            const costChanged = !!simpleDiff(A.cost_model || null, B.cost_model || null);

                            if (!(titleChanged || summaryChanged || bodyChanged || sectionsChanged || rulesChanged || costChanged)) {
                              return <div className="text-xs text-green-400 bg-green-900/20 border border-green-800 rounded p-2">No differences detected.</div>;
                            }

                            return (
                              <div className="space-y-2">
                                {titleChanged && (
                                  <div className="border border-gray-700 rounded bg-gray-800">
                                    <div className="text-xs font-semibold text-gray-400 px-2 py-1 border-b border-gray-700">Title</div>
                                    <div className="grid grid-cols-1 md:grid-cols-2">
                                      <div className="p-2 text-sm bg-red-900/20 border-r border-gray-700 text-gray-300">{A.title || <span className="text-gray-500">(empty)</span>}</div>
                                      <div className="p-2 text-sm bg-green-900/20 text-gray-300">{B.title || <span className="text-gray-500">(empty)</span>}</div>
                                    </div>
                                  </div>
                                )}

                                {summaryChanged && (
                                  <div className="border border-gray-700 rounded bg-gray-800">
                                    <div className="text-xs font-semibold text-gray-400 px-2 py-1 border-b border-gray-700">Summary</div>
                                    <div className="grid grid-cols-1 md:grid-cols-2">
                                      <pre className="p-2 m-0 text-xs bg-red-900/20 border-r border-gray-700 whitespace-pre-wrap break-words text-gray-300">{A.summary || ''}</pre>
                                      <pre className="p-2 m-0 text-xs bg-green-900/20 whitespace-pre-wrap break-words text-gray-300">{B.summary || ''}</pre>
                                    </div>
                                  </div>
                                )}

                                {bodyChanged && (
                                  <div className="border border-gray-700 rounded bg-gray-800">
                                    <div className="text-xs font-semibold text-gray-400 px-2 py-1 border-b border-gray-700">Body (Markdown)</div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                                      <div className="p-3 prose prose-invert prose-sm max-w-none border-r border-gray-700 bg-red-900/10">
                                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{A.content?.body || ''}</ReactMarkdown>
                                      </div>
                                      <div className="p-3 prose prose-invert prose-sm max-w-none bg-green-900/10">
                                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{B.content?.body || ''}</ReactMarkdown>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {sectionsChanged && (
                                  <div className="border border-gray-700 rounded bg-gray-800">
                                    <div className="text-xs font-semibold text-gray-400 px-2 py-1 border-b border-gray-700">Sections</div>
                                    <div className="space-y-2 p-2">
                                      {sectionDiffItems.map(({ key, a, b }) => {
                                        const equal = JSON.stringify(a || null) === JSON.stringify(b || null);
                                        if (equal) return null;
                                        return (
                                          <div key={key} className="border border-gray-700 rounded">
                                            <div className="text-xs px-2 py-1 border-b border-gray-700 bg-gray-900/30 flex items-center justify-between">
                                              <span className="font-medium text-gray-300">{key}</span>
                                              <span className="text-[10px] text-gray-500">
                                                {(a && b) ? 'modified' : (a ? 'removed' : 'added')}
                                              </span>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2">
                                              <div className="p-2 border-r border-gray-700">
                                                <div className="text-[11px] text-gray-500 mb-1">Title</div>
                                                <div className="text-sm bg-red-900/20 p-2 rounded text-gray-300">{a?.title || <span className="text-gray-500">(empty)</span>}</div>
                                                <div className="text-[11px] text-gray-500 mt-2 mb-1">Body</div>
                                                <div className="prose prose-invert prose-sm max-w-none bg-red-900/20 p-2 rounded">
                                                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{a?.body || ''}</ReactMarkdown>
                                                </div>
                                              </div>
                                              <div className="p-2">
                                                <div className="text-[11px] text-gray-500 mb-1">Title</div>
                                                <div className="text-sm bg-green-900/20 p-2 rounded text-gray-300">{b?.title || <span className="text-gray-500">(empty)</span>}</div>
                                                <div className="text-[11px] text-gray-500 mt-2 mb-1">Body</div>
                                                <div className="prose prose-invert prose-sm max-w-none bg-green-900/20 p-2 rounded">
                                                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{b?.body || ''}</ReactMarkdown>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}

                                {rulesChanged && (
                                  <div className="border border-gray-700 rounded bg-gray-800">
                                    <div className="text-xs font-semibold text-gray-400 px-2 py-1 border-b border-gray-700">Eligibility Rules</div>
                                    <div className="grid grid-cols-1 md:grid-cols-2">
                                      <pre className="p-2 m-0 text-xs overflow-auto whitespace-pre-wrap break-words border-r border-gray-700 bg-red-900/20 text-gray-300">{JSON.stringify(A.eligibility_rules || [], null, 2)}</pre>
                                      <pre className="p-2 m-0 text-xs overflow-auto whitespace-pre-wrap break-words bg-green-900/20 text-gray-300">{JSON.stringify(B.eligibility_rules || [], null, 2)}</pre>
                                    </div>
                                  </div>
                                )}

                                {costChanged && (
                                  <div className="border border-gray-700 rounded bg-gray-800">
                                    <div className="text-xs font-semibold text-gray-400 px-2 py-1 border-b border-gray-700">Cost Model</div>
                                    <div className="grid grid-cols-1 md:grid-cols-2">
                                      <pre className="p-2 m-0 text-xs overflow-auto whitespace-pre-wrap break-words border-r border-gray-700 bg-red-900/20 text-gray-300">{JSON.stringify(A.cost_model || null, null, 2)}</pre>
                                      <pre className="p-2 m-0 text-xs overflow-auto whitespace-pre-wrap break-words bg-green-900/20 text-gray-300">{JSON.stringify(B.cost_model || null, null, 2)}</pre>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>

                    <div className="border border-gray-700 rounded-lg p-4 space-y-4 bg-gray-900/30">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-gray-300">Content Sections</h4>
                        <ActionButton onClick={addSection} disabled={selectedVersion.status === 'published'}>
                          Add Section
                        </ActionButton>
                      </div>
                      {sections.length === 0 ? (
                        <div className="text-xs text-gray-500">No sections yet. Add your first section.</div>
                      ) : (
                        <div className="space-y-3">
                          {sections.map((section, index) => (
                            <div key={section.key || `section_${index}`} className="bg-gray-800 border border-gray-700 rounded p-3 space-y-2">
                              <div className="flex items-center gap-2">
                                <input
                                  className="border border-gray-600 rounded px-2 py-1 text-xs flex-1 bg-gray-700 text-white"
                                  placeholder="Section Key (e.g., overview)"
                                  value={section.key}
                                  onChange={(e) => updateSection(index, 'key', e.target.value)}
                                  disabled={selectedVersion.status === 'published'}
                                />
                                <button
                                  onClick={() => removeSection(index)}
                                  disabled={selectedVersion.status === 'published'}
                                  className="text-xs text-red-500 hover:text-red-700"
                                >
                                  Remove
                                </button>
                              </div>
                              <input
                                className="border border-gray-600 rounded px-3 py-2 text-sm w-full bg-gray-700 text-white"
                                placeholder="Section Title"
                                value={section.title}
                                onChange={(e) => updateSection(index, 'title', e.target.value)}
                                disabled={selectedVersion.status === 'published'}
                              />
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <textarea
                                  className="border border-gray-600 rounded px-3 py-2 text-sm h-32 w-full font-mono bg-gray-700 text-white"
                                  placeholder="Section body (Markdown)"
                                  value={section.body}
                                  onChange={(e) => updateSection(index, 'body', e.target.value)}
                                  disabled={selectedVersion.status === 'published'}
                                />
                                <div className="border border-gray-700 rounded p-3 bg-gray-900/50 prose prose-invert prose-sm max-w-none overflow-y-auto h-32">
                                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                                    {section.body || ''}
                                  </ReactMarkdown>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="border border-gray-700 rounded-lg p-4 space-y-4 bg-gray-900/30">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-gray-300">Eligibility Rules</h4>
                        <ActionButton onClick={addEligibilityRule} disabled={selectedVersion.status === 'published'}>
                          Add Rule
                        </ActionButton>
                      </div>
                      {eligibilityRules.length === 0 ? (
                        <div className="text-xs text-gray-500">No eligibility rules yet.</div>
                      ) : (
                        <div className="space-y-3">
                          {eligibilityRules.map((rule, index) => (
                            <div key={rule.id || `rule_${index}`} className="bg-gray-800 border border-gray-700 rounded p-3 space-y-2">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <input
                                  className="border border-gray-600 rounded px-2 py-1 text-xs bg-gray-700 text-white"
                                  placeholder="Attribute (e.g., age, ielts)"
                                  value={rule.attribute}
                                  onChange={(e) => updateEligibilityRule(index, 'attribute', e.target.value)}
                                  disabled={selectedVersion.status === 'published'}
                                />
                                <select
                                  className="border border-gray-600 rounded px-2 py-1 text-xs bg-gray-700 text-white"
                                  value={rule.operator}
                                  onChange={(e) => updateEligibilityRule(index, 'operator', e.target.value)}
                                  disabled={selectedVersion.status === 'published'}
                                >
                                  {operatorOptions.map((op) => (
                                    <option key={op} value={op}>{op}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <input
                                  className="border border-gray-600 rounded px-2 py-1 text-xs bg-gray-700 text-white"
                                  placeholder="Value"
                                  value={rule.value}
                                  onChange={(e) => updateEligibilityRule(index, 'value', e.target.value)}
                                  disabled={selectedVersion.status === 'published'}
                                />
                                <select
                                  className="border border-gray-600 rounded px-2 py-1 text-xs bg-gray-700 text-white"
                                  value={rule.severity}
                                  onChange={(e) => updateEligibilityRule(index, 'severity', e.target.value)}
                                  disabled={selectedVersion.status === 'published'}
                                >
                                  {severityOptions.map((sev) => (
                                    <option key={sev} value={sev}>{sev}</option>
                                  ))}
                                </select>
                              </div>
                              <textarea
                                className="border border-gray-600 rounded px-2 py-1 text-xs w-full bg-gray-700 text-white"
                                placeholder="Notes or guidance"
                                value={rule.note || ''}
                                onChange={(e) => updateEligibilityRule(index, 'note', e.target.value)}
                                disabled={selectedVersion.status === 'published'}
                              />
                              <div className="flex justify-end">
                                <button
                                  onClick={() => removeEligibilityRule(index)}
                                  disabled={selectedVersion.status === 'published'}
                                  className="text-xs text-red-500 hover:text-red-700"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="border border-gray-700 rounded-lg p-4 space-y-4 bg-gray-900/30">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-gray-300">Cost Components</h4>
                        <ActionButton onClick={addCostComponent} disabled={selectedVersion.status === 'published'}>
                          Add Cost Line
                        </ActionButton>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-600">
                        <label className="font-medium uppercase">Currency</label>
                        <input
                          className="border border-gray-600 rounded px-2 py-1 text-xs w-24 bg-gray-700 text-white"
                          value={costModel.currency}
                          onChange={(e) => handleCostCurrencyChange(e.target.value.toUpperCase())}
                          disabled={selectedVersion.status === 'published'}
                        />
                      </div>
                      {(costModel.components || []).length === 0 ? (
                        <div className="text-xs text-gray-500">No cost components yet.</div>
                      ) : (
                        <div className="space-y-3">
                          {costModel.components.map((component, index) => (
                            <div key={component.id || `cost_${index}`} className="bg-gray-800 border border-gray-700 rounded p-3 space-y-2">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <input
                                  className="border border-gray-600 rounded px-2 py-1 text-xs bg-gray-700 text-white"
                                  placeholder="Label (e.g., Tuition)"
                                  value={component.label}
                                  onChange={(e) => updateCostComponent(index, 'label', e.target.value)}
                                  disabled={selectedVersion.status === 'published'}
                                />
                                <input
                                  className="border border-gray-600 rounded px-2 py-1 text-xs bg-gray-700 text-white"
                                  placeholder="Payable to (gov/college)"
                                  value={component.payable_to}
                                  onChange={(e) => updateCostComponent(index, 'payable_to', e.target.value)}
                                  disabled={selectedVersion.status === 'published'}
                                />
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                <input
                                  type="number"
                                  className="border border-gray-600 rounded px-2 py-1 text-xs bg-gray-700 text-white"
                                  placeholder="Min"
                                  value={component.min}
                                  onChange={(e) => updateCostComponent(index, 'min', e.target.value)}
                                  disabled={selectedVersion.status === 'published'}
                                />
                                <input
                                  type="number"
                                  className="border border-gray-600 rounded px-2 py-1 text-xs bg-gray-700 text-white"
                                  placeholder="Max"
                                  value={component.max}
                                  onChange={(e) => updateCostComponent(index, 'max', e.target.value)}
                                  disabled={selectedVersion.status === 'published'}
                                />
                                <label className="text-xs flex items-center gap-1">
                                  <input
                                    type="checkbox"
                                    checked={component.recurring || false}
                                    onChange={(e) => updateCostComponent(index, 'recurring', e.target.checked)}
                                    disabled={selectedVersion.status === 'published'}
                                  />
                                  Recurring
                                </label>
                              </div>
                              <textarea
                                className="border border-gray-600 rounded px-2 py-1 text-xs w-full bg-gray-700 text-white"
                                placeholder="Notes"
                                value={component.notes || ''}
                                onChange={(e) => updateCostComponent(index, 'notes', e.target.value)}
                                disabled={selectedVersion.status === 'published'}
                              />
                              <div className="flex justify-end">
                                <button
                                  onClick={() => removeCostComponent(index)}
                                  disabled={selectedVersion.status === 'published'}
                                  className="text-xs text-red-500 hover:text-red-700"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      <label className="text-xs font-semibold text-gray-400 uppercase">Change Notes</label>
                      <textarea
                        className="border border-gray-600 rounded px-3 py-2 text-sm h-16 bg-gray-700 text-white"
                        value={form.changeNotes}
                        onChange={(e) => handleFieldChange('changeNotes', e.target.value)}
                      />
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-700">
                      <div className="text-xs text-gray-400">
                        Version {selectedVersion.version_number} • Updated {new Date(selectedVersion.updated_at).toLocaleString()}
                      </div>
                      <div className="flex items-center gap-2">
                        <ActionButton
                          onClick={handleSave}
                          disabled={saving || selectedVersion.status === 'published'}
                        >
                          {saving ? 'Saving…' : 'Save Draft'}
                        </ActionButton>
                        <ActionButton onClick={handleClone} disabled={cloning}>
                          {cloning ? 'Cloning…' : 'Clone'}
                        </ActionButton>
                        <ActionButton
                          onClick={handlePublish}
                          variant="primary"
                          disabled={publishing || selectedVersion.status === 'published'}
                        >
                          {publishing ? 'Publishing…' : 'Publish'}
                        </ActionButton>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PathwayVersionsModal;
